import { FilterToggleButton, useAnalytics } from '@ecosystem/newron-client-lib';
import { feedback, layouts } from '@ecosystem/newron-design-system';
import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  RelativePeriod,
  RelativePeriodLabel,
  RelativePeriodMapping,
} from '@components/comparison-period-selector/Types';
import InfoModal from '@components/info-modal/InfoModal';
import LoadingOverlay from '@components/loading-overlay/LoadingOverlay';
import {
  constructCountryOption,
  onRetailerChange,
  ResetSequenceFuncParam,
} from '@components/multi-banner-retailer-selector/MultiBannerRetailerSelectorUtils';
import PageHeader from '@components/page-header/PageHeader';
import { supplierReviewLink, TradingAssistantTabView } from '@components/tab-nav/TabConfig';
import TabNav, { NavigationLinkProps } from '@components/tab-nav/TabNav';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { ClientSubscription } from '@models/Opa';
import { RetailerClient } from '@models/RetailerClient';
import { BaseTimePeriod, FilterParam, MainFilters, TimePeriod } from '@services/FiltersTypes';
import {
  actionCreators,
  actionCreatorsMarketPremium,
  actionCreatorsMarketPremiumClientState,
  actionCreatorsRetailerClient,
  actionCreatorsSideFilters,
  actionCreatorsTA,
} from '@state/Index';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import { tradingAssistantResponseConfig } from '@utils/FilterSettings';
import {
  checkIfCountryExist,
  checkPGsByCountryPreconditionWithProductType,
  findDefaultFilterBySelectedCountries,
  getFeatureValueBySubscription,
  getFeatureValueForRetailerTA,
  getFeatureValuesByPeriodicity,
  getFiltersInitialState,
  getInitialAppliedCountries,
  getInitialSelectedAddFeat,
  getInitialSelectedAddFeatValues,
  getInitialSelectedSegFeat,
  getInitialSelectedSegFeatValues,
  getLatestPeriod,
  getSubscriptionByCountryAndProductType,
  initialFeature,
  isRemovePricebandFilterForNonLocalCurrency,
  isValidTimeFrameAndPeriod,
  removeUnsavedFilters,
  RetailerDetailsTA,
  UpdateAddFeatSegFeatValues,
  updatePgsByCountryOnCountryApplyWithProductType,
  updatePgsByCountryOnCountrySelectWithProductType,
  updateSelectedAppliedCountryStateByAppliedFilter,
  updateSelectedAppliedPgStateByAppliedFilter,
  updateSelectedProductGroup,
  validateStoreFilterSettings,
} from '@utils/FilterUtils';
import saveErrorLog, { ErrorTagEnum } from '@utils/LogUtil';
import { isAuthorized } from '@utils/Permission';
import {
  constructFeatureValueParam,
  getAndUpdateFeatureSelector,
  getFeatureSelector,
  SELECT_FEATURE_OPTION,
} from '@utils/hook/selector/ShowPerformanceWithinSelector';
import {
  PAGE_VIEW_TRACKING_TITLE,
  pageViewTrackingFunction,
  PageViewTrackingFunctionParam,
} from '@utils/page-analytics/model/PageViewedTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  CountryFilterPair,
  FeatureOption,
  FilterKeys,
  Filters,
  FilterTitle,
  Option,
  PageName,
  PageType,
  PageTypeAndName,
  SelectedFilters,
  SubSection,
} from '@utils/pageConfig/Types';
import PageInfo from '../info/PageInfo';
import { ProductDataType } from '../product-analyzer/Product';
import { Unauthorized } from '../unauthorized/Unauthorized';
import SupplierReview from './supplier-review/SupplierReview';
import { IRootState } from '@state/reducers/Reducers';
import { WeeklyMonthlyFeatures } from '@models/TradingAssistant';
import { byCountry } from '@state/reducers/initial-filter/InitialFiltersPredicates';
import { getFallbackPeriodCombo } from '@utils/period-set/PeriodSetFallback';
import { tradingAssistantConfiguration } from '../Configuration';
import {
  BrandDependencyKpi,
  BrandDependencySort,
  INITIAL_BRAND_FILTER,
  INITIAL_BY_SEGMENT_OPTION,
} from '@models/BrandDependency';
import { getEffectiveProductType } from '@models/SubscribedProductType';
import { useUserPersistedFilters } from '@utils/hook/saveFilter/UseUserPersistedFilters';
import { getGrandTotalValue } from '@utils/TotalUtils';
import { getProductGroupsMasterByCountry } from '@services/ProductGroup.service';
import {
  getAndUpdatePeriod,
  getDefaultValueForDerivablePeriodicity,
  getPeriodicityForMATAndYTD,
  isBaseTimePeriodFormat,
} from '@utils/PeriodUtils';
import { useUpdatePerfPeriodSet } from '@utils/period-set/UseUpdatePerfPeriodSet';
import TradingAssistantCopyViewButton from './copy-view-button/TradingAssistantCopyViewButton';
import { TradingAssistantRestoreLocalSavedViewProps, TradingAssistantViewState } from '@models/SavedViews';
import { useRestoreTradingAssistantSavedView } from '@utils/hook/saveViews/UseRestoreTradingAssistantSavedView';
import { TrackingGeneralProp } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { useInitializedPeriodSet } from '@utils/period-set/UseInitializedPeriodSet';
import { GrowthTypes } from '@ecosystem/formatters';
import { updateMainFiltersMPGrowthType } from '@state/actions/main-filter-market-premium/MainFiltersMarketPremiumAction';
import { initialiseBaseFilters } from '@utils/InitBaseFilters';
import { NO_OPTIONS } from '@utils/StringConstants';
import { getCurrencyExchangeRate } from '@services/Currency.service';
import { getExchangeRate } from '@utils/currencyUtils/CurrencyUtils';
import { formatPriceBandAsPerExchangeRate } from '@utils/Formatters';
import { cloneDeep } from 'lodash';

interface Props {
  retailerClients: RetailerClient[];
}

const TradingAssistant: React.FC<Props> = ({ retailerClients }) => {
  const pagesConfig = usePageConfig();
  const { refetch } = useUserPersistedFilters();
  const { Page } = layouts;
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isAuthorizedMarketPremiumUser = isAuthorized(pagesConfig.marketPremiumClientSubscription);
  const isAuthorizedKadUser = isAuthorized(pagesConfig.retailKadClientSubscription);
  const isAuthorizedUser = isAuthorizedMarketPremiumUser || isAuthorizedKadUser;

  const [loadingOverlay, setLoadingOverlay] = useState<boolean>(false);

  const retailerClientState = useSelector((state: IRootState) => state.marketPremiumRetailerClient);

  const initialFiltersProductsState = useSelector((state: IRootState) => state.initialFiltersProducts);
  const hitlistsRetailerClientState = useSelector((state: IRootState) => state.hitlistsRetailerClient);

  const { updateMarketPremiumRetailerClient } = bindActionCreators(actionCreatorsMarketPremiumClientState, dispatch);
  const { updateMainFiltersMarketPremium } = bindActionCreators(actionCreatorsMarketPremium, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);
  const { updateMainFiltersTA } = bindActionCreators(actionCreatorsTA, dispatch);
  const { updateRetailerClient } = bindActionCreators(actionCreatorsRetailerClient, dispatch);
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const initialFiltersState = useSelector((state: IRootState) => state.initialFiltersMarketPremium);
  const mainFilterStateMarketPremium = useSelector((state: IRootState) => state.mainFiltersMarketPremium);

  const mainFilterStateProducts = useSelector((state: IRootState) => state.mainFiltersProducts);
  const sideFiltersState: SideFiltersState = useSelector((state: IRootState) => state.sideFilters);

  const subscribedProductTypes = useSelector(
    (state: IRootState) => state.subscribedProductTypes.subscribedProductTypes,
  );
  const effectiveProductType = getEffectiveProductType(subscribedProductTypes, PageName.TRADING_ASSISTANT);

  const { defaultCountry: initialCountry, defaultCurrency: initialCurrency } = initialiseBaseFilters(
    pagesConfig,
    effectiveProductType,
    updateSideFilters,
    PageType.TRADING_ASSISTANT,
  );

  const [appliedCountries, setAppliedCountries] = useState<Option[]>(initialCountry);

  const [selectedCountries, setSelectedCountries] = useState<Option[]>(initialCountry);

  const [timeFrame, setTimeFrame] = useState<Periodicity>(mainFilterStateMarketPremium.timeFrame);
  const [comparisonPeriod, setComparisonPeriod] = useState<RelativePeriod | BaseTimePeriod>(
    mainFilterStateMarketPremium.comparisonPeriod,
  );
  const selectedGrowthType = mainFilterStateMarketPremium.selectedGrowthType;
  const setSelectedGrowthType = (newGrowthType: string) => {
    updateMainFiltersMPGrowthType(newGrowthType as GrowthTypes);
  };
  const [feature, setFeature] = useState<FeatureOption>(mainFilterStateMarketPremium.feature);
  const [isDeepDiveView, setIsDeepDiveView] = useState<boolean>(false);

  const allFilters = [...pagesConfig.appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters];
  const pgInitialState = getFiltersInitialState(allFilters, FilterKeys.PRODUCT_GROUP);
  const addFeatInitialState = getFiltersInitialState(allFilters, FilterKeys.ADDFEAT);
  const segFeatInitialState = getFiltersInitialState(allFilters, FilterKeys.SEGFEAT);
  const [defaultFilters, setDefaultFilters] = useState<MainFilters[]>(initialFiltersState.initialFilters);
  const [filters, setFilters] = useState<MainFilters>(
    findDefaultFilterBySelectedCountries(
      pagesConfig.appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters,
      defaultFilters,
    ),
  );

  const [selectedTabLink, setSelectedTabLink] = useState<NavigationLinkProps>(supplierReviewLink);

  const [selectedProductGroups, setSelectedProductGroups] = useState<Option[] | undefined>(pgInitialState?.options);

  // Stores the applied product group. APIs use this state as query param
  const [appliedProductGroups, setAppliedProductGroups] = useState<Option[]>(
    pgInitialState !== undefined ? pgInitialState.options : [],
  );

  // Weekly and monthly feature values
  const [weeklyMonthlyFeatures, setWeeklyMonthlyFeatures] = useState<WeeklyMonthlyFeatures>({
    weekly: [],
    monthly: [],
  });
  const { customMonthsPeriodState, customWeeksPeriodState } = useInitializedPeriodSet(filters.timePeriod, true);
  const { updatePeriod, updateCustomMonthsPeriodState, updateCustomWeeksPeriodState } = useUpdatePerfPeriodSet();

  const [, setSelectedAddFeatOpp] = useState<string | undefined>(getInitialSelectedAddFeat(addFeatInitialState)); //NOSONAR typescript:S6754

  const [, setSelectedAddFeatOppValues] = useState<Option[] | undefined>( //NOSONAR typescript:S6754
    getInitialSelectedAddFeatValues(addFeatInitialState),
  );

  const [, setSelectedSegFeatOpp] = useState<string | undefined>(getInitialSelectedSegFeat(segFeatInitialState)); //NOSONAR typescript:S6754

  const [, setSelectedSegFeatOppValues] = useState<Option[] | undefined>( //NOSONAR typescript:S6754
    getInitialSelectedSegFeatValues(segFeatInitialState),
  );

  const [period, setPeriod] = useState<BaseTimePeriod>(mainFilterStateMarketPremium.period);
  const [isCountryFirstLoad, setIsCountryFirstLoad] = useState<boolean>(true);
  const [, setCountryChanged] = useState<boolean>(false); //NOSONAR typescript:S6754

  const [isFetchingFeatureValues, setIsFetchingFeatureValues] = useState<boolean>(false);

  // State of first load page
  const [onPageFirstLoad, setOnPageFirstLoad] = useState<boolean>(true);

  const [brandFilterOptions, setBrandFilterOptions] = useState<Filters>(INITIAL_BRAND_FILTER);

  const [viewingBrand, setViewingBrand] = useState<string>('');
  const [wasRestored, setWasRestored] = useState(false);
  const [kpi, setKpi] = useState<BrandDependencyKpi>(BrandDependencyKpi.marketRevenue);
  const [sortBy, setSortBy] = useState<BrandDependencySort>(BrandDependencySort.retailerShareOfBrand);
  const setViewingBrandState = (brand: string, source?: 'restore' | 'kpi_selector' | 'brand_selector') => {
    // if the source is 'restore', it means the brand is being restored from a saved view
    if (source === 'restore') {
      setViewingBrand(brand);
      setWasRestored(true);
      return;
    }
    // if brand has already been restored from a saved view
    if (wasRestored) {
      const isSelector = source === 'kpi_selector' || source === 'brand_selector';
      // only set brand if it is being set from a selector
      // (kpi_selector or brand_selector), update the state
      if (isSelector) {
        setViewingBrand(brand);
        setWasRestored(false);
      }
      // We have prevented updating the brand again after it has been restored from a saved view,
      // unless the update comes explicitly from a selector.
      // This ensures the restored brand value is preserved and not unintentionally overwritten.
      return;
    }
    // default case, set the viewing brand state
    setViewingBrand(brand);
  };

  const [isUpdateFeaturesApiCall, setIsUpdateFeaturesApiCall] = useState<boolean>(false);
  const [pgsByAppliedCountries, setPgsByAppliedCountries] = useState<CountryFilterPair[]>([]);
  const CURRENT_TOTAL_NAME = getGrandTotalValue();

  const pageViewTracking = () => {
    const trackingParam: PageViewTrackingFunctionParam = {
      eventTitle: PAGE_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      retailerClientState: retailerClientState,
      track: track,
      pageType: PageType.TRADING_ASSISTANT,
      pageName: PageName.TRADING_ASSISTANT,
      access: pagesConfig.access,
      subSection: SubSection.TRADING_ASSISTANT_SUPPLIER_REVIEW,
      initialLocationId: getInitialAppliedCountries(pagesConfig, PageType.TRADING_ASSISTANT, effectiveProductType),
    };
    pageViewTrackingFunction(trackingParam);
  };

  const getFeatureValueParam = (): FilterParam => {
    return {
      productType: effectiveProductType,
      selectedProductGroup: selectedProductGroups,
      initialFilters: defaultFilters,
      selectedCountries: selectedCountries,
      pagesConfig: pagesConfig,
      responseConfig: tradingAssistantResponseConfig,
      allFilters: allFilters,
      subscriptions: getSubscriptionByCountryAndProductType(pagesConfig, selectedCountries, effectiveProductType),
      includeCommonFeaturesOnly: true,
    };
  };

  const validateBeforeFetchFeatures = () =>
    !onPageFirstLoad &&
    !isCountryFirstLoad &&
    isValidTimeFrameAndPeriod(timeFrame, period) &&
    appliedProductGroups &&
    checkIfCountryExist(selectedCountries, pagesConfig, effectiveProductType) &&
    !isFetchingFeatureValues;

  const getNewSideFiltersStateWithBrand = () => {
    let selectedFilters: Filters[] = [...sideFiltersState.appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters];

    // Add a Brand filter if it doesn't exist in the side filters state, so that the
    // base key checking will work correctly
    if (!selectedFilters.find((fil) => fil.filterKey === FilterKeys.BRAND)) {
      selectedFilters = [
        ...sideFiltersState.appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters,
        {
          dataType: 'Brand',
          feature: 'Brand',
          filterTitle: FilterTitle.BRAND,
          filterKey: FilterKeys.BRAND,
          options: [],
          disabled: false,
        },
      ];
    }
    const selectedFilter: SelectedFilters = {
      pageType: PageType.TRADING_ASSISTANT,
      selectedFilters,
    };

    return {
      appliedFilters: [...sideFiltersState.appliedFilters].map((elem, idx) =>
        idx === PageType.TRADING_ASSISTANT ? selectedFilter : elem,
      ),
    };
  };

  //Initial load
  useEffect(() => {
    setLoadingOverlay(true);

    /**
     * (Store, Router) Refactor this component to externalize the logic for setting the page type and name,
     * updating the page view, and assigning the client ID. These actions should be moved to the store
     * and triggered during navigation (using react router) from the app component.
     */

    const pageTypeAndName: PageTypeAndName = {
      pageType: PageType.TRADING_ASSISTANT,
      pageName: PageName.TRADING_ASSISTANT,
    };

    //Update page viewing
    updateMarketPremiumRetailerClient({
      ...retailerClientState,
      pageViewing: PageName.TRADING_ASSISTANT,
    });
    updateRetailerClient({ ...retailerClientState, pageViewing: PageName.TRADING_ASSISTANT });
    pagesConfig.setClientId(retailerClientState.clientId);
    pagesConfig.setNavigateFromCategoryLeaderboard(false);
    pagesConfig.setIsNavigateFromOpportunity(false);
    pagesConfig.setIsNavigateFromPerfAnalysis(false);

    validateStoreFilterSettings(sideFiltersState, pagesConfig, pageTypeAndName);

    // Since the currency filter is available on this page, we need to validate for any selected
    // priceband feature option.
    if (
      isRemovePricebandFilterForNonLocalCurrency(
        pagesConfig,
        sideFiltersState.appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters,
        effectiveProductType,
        feature,
      ) ||
      feature.dataType === NO_OPTIONS
    ) {
      setFeature(SELECT_FEATURE_OPTION); // reset the feature
    }

    pageViewTracking();
    setLoadingOverlay(false);
    updateRetailerClient({ ...retailerClientState, pageViewing: PageName.TRADING_ASSISTANT });
    setOnPageFirstLoad(false);
  }, []);

  useEffect(() => {
    setDefaultFilters(initialFiltersState.initialFilters);
  }, [initialFiltersState.initialFilters]);

  useEffect(() => {
    if (!wasRestored && isValidTimeFrameAndPeriod(timeFrame, period)) {
      const updatedMainFilter = {
        timeFrame,
        period,
        comparisonPeriod,
        selectedGrowthType,
        feature,
        customWeeksPeriodState,
        customMonthsPeriodState,
      };
      updateMainFiltersMarketPremium({
        ...updatedMainFilter,
      });

      if (updatedMainFilter.feature.feature === 'Select') {
        updatedMainFilter.feature = initialFeature;
      }
      updateMainFilters(updatedMainFilter);
      updateMainFiltersTA(updatedMainFilter);
    }
  }, [feature, timeFrame, period, comparisonPeriod, selectedGrowthType]);

  /**
   *   Update the feature value at side filter if time frame changed
   */
  useEffect(() => {
    if (onPageFirstLoad || isCountryFirstLoad) {
      return;
    }

    // Reset unapply fields at side filters after applied/changed new time frame.
    const isResetSideFilter: boolean = removeUnsavedFilters(pagesConfig);

    if (isResetSideFilter) {
      // Reset local selected states (Country and PG)
      setSelectedCountries(appliedCountries);
      setSelectedProductGroups(appliedProductGroups);
    }

    // update feature
    const allFeatureValuesOptions = getFeatureValuesByPeriodicity(weeklyMonthlyFeatures, timeFrame);

    // if dataType is empty, means response will definitely be empty. This code block prevents the API from getting called and returning 400/403
    getAndUpdateFeatureSelector(allFeatureValuesOptions, feature, setFeature);
  }, [timeFrame, period]);

  useEffect(() => {
    if (!validateBeforeFetchFeatures()) {
      return;
    }

    const fetchFeatureValues = async () => {
      setIsFetchingFeatureValues(true);
      const newFeatureValues = await updateFeatureValueByTimeFrame(getFeatureValueParam());
      const brandFilters = newFeatureValues.find((featureValue) => featureValue.filterKey === FilterKeys.BRAND)!;
      setBrandFilterOptions(brandFilters);
      const featureOption = getFeatureSelector(newFeatureValues, feature, [], PageName.TRADING_ASSISTANT);
      setFeature(featureOption);
      setIsFetchingFeatureValues(false);
    };

    if (!wasRestored) {
      fetchFeatureValues();
    }
  }, [timeFrame, appliedCountries, appliedProductGroups]);

  /**
   * Update the side filter fields after selected filters.
   */
  useEffect(() => {
    // Skip if applied new time frame
    if (!validateBeforeFetchFeatures() || isUpdateFeaturesApiCall) {
      setIsUpdateFeaturesApiCall(false);
      return;
    }

    updateFeatureValueByTimeFrame(getFeatureValueParam(), false);
  }, [selectedProductGroups]);

  const resetSequenceFunc = useCallback(
    async ({
      defaultClientId,
      subscribedCountries,
      hitlistsSubscriptions,
      hitlistsSubscribedCountries,
      defaultCountry,
      pagesConfigAllFilters,
      filtersDataBasedOnCountry,
      currTimeFrame,
      selectedProductGroup: selectedPG,
    }: ResetSequenceFuncParam) => {
      // Do not simply move the setState below, it will trigger unintended side effects
      pagesConfig.setMarketPremiumClientId(defaultClientId);
      pagesConfig.setMarketPremiumClientCountries(subscribedCountries);
      if (hitlistsSubscriptions.length !== 0) {
        pagesConfig.setHitlistsClientCountries(hitlistsSubscribedCountries);
        pagesConfig.setHitlistsClientId(defaultClientId);
      }
      pagesConfig.setClientId(defaultClientId);
      pagesConfig.setClientCountries(subscribedCountries);
      setSelectedCountries(constructCountryOption(defaultCountry));
      pagesConfig.setSelectedFilters(pagesConfigAllFilters);
      setFilters(filtersDataBasedOnCountry);
      setAppliedCountries(constructCountryOption(defaultCountry));

      const featureValuesForTA = await getFeatureValueForRetailerTA({
        clientId: defaultClientId,
        productType: effectiveProductType,
        productDataType: ProductDataType.PerformanceAnalyzer,
        selectedCountries: constructCountryOption(defaultCountry),
        timeFrame: currTimeFrame,
        selectedProductGroups: selectedPG ?? [],
        currentTotalName: getGrandTotalValue(),
        pageType: pagesConfig.pageType,
      } as RetailerDetailsTA);

      setFeature(getFeatureSelector(featureValuesForTA, feature, [], PageName.TRADING_ASSISTANT));
      setTimeFrame(currTimeFrame);
    },
    [],
  );

  /**
   * To handle the change event when select other retailer in the retailer dropdown - reset the necessary states in order to refresh the reports
   * @param subscriptions client subscription from opa policy
   */
  const onChangeHandler = async (subscriptions: ClientSubscription[]) => {
    await onRetailerChange(
      subscriptions,
      {
        pagesConfig,
        appliedCountries,
        retailerClients,
        setLoadingOverlay,
        timeFrame,
        pageName: PageName.TRADING_ASSISTANT,
        selectedGrowthType,
        selectedGrowthTypeForProducts: mainFilterStateProducts.selectedGrowthType,
        setDefaultFilters,
        feature,
        productType: effectiveProductType,
        updateRetailerClientAction: updateMarketPremiumRetailerClient,
        resetSequenceFunc,
      },
      dispatch,
    );
    refetch();
  };

  /**
   * A method to update the feature value at the side filter which filter by selected/applied country, product group and time frame.
   *
   * @param param Parameter for feature value
   * @param isUpdateFeatValOptionsState is a flag to update the feature values states or not
   */
  const updateFeatureValueByTimeFrame = async (
    param: FilterParam,
    isUpdateFeatValOptionsState: boolean = true,
  ): Promise<Filters[]> => {
    const weeklyFeatures = await fetchFeatureValueByParamAndPeriodicity(param, Periodicity.Weekly);
    const monthlyFeatures = await fetchFeatureValueByParamAndPeriodicity(param, Periodicity.Monthly);
    if (isUpdateFeatValOptionsState) {
      setWeeklyMonthlyFeatures({
        weekly: weeklyFeatures,
        monthly: monthlyFeatures,
      });
    }
    const featureValuesMap = {
      [Periodicity.Weekly]: weeklyFeatures,
      [Periodicity.Monthly]: monthlyFeatures,
    };
    return featureValuesMap[getDefaultValueForDerivablePeriodicity(timeFrame)] ?? [];
  };

  /**
   * A method (Wrap with useCallback to avoid change on every render) to fetch the feature value by filter param and time frame.
   *
   *
   * @param requestParam Parameter for feature value
   * @param requestPeriodicity Time frame
   */
  const fetchFeatureValueByParamAndPeriodicity = useCallback(
    async (requestParam: FilterParam, requestPeriodicity: Periodicity) => {
      try {
        const response =
          (await getFeatureValueBySubscription(
            constructFeatureValueParam(requestParam, requestPeriodicity),
            timeFrame,
            CURRENT_TOTAL_NAME,
          )) ?? [];
        return requestParam.selectedProductGroup && requestParam.selectedProductGroup.length > 1
          ? response.filter((feat: { filterKey: FilterKeys }) => feat.filterKey !== FilterKeys.PRICEBAND)
          : response;
      } catch (error) {
        saveErrorLog(error, `${PageName.TRADING_ASSISTANT} - Unable to get feature values response.`, {
          category: ErrorTagEnum.ApiError,
          requestPayload: {
            clientId: retailerClientState.clientId,
            productGroup: selectedProductGroups,
            period,
            comparisonPeriod,
          },
        });
        return [];
      }
    },
    [timeFrame],
  );

  /*
   * Fetch product groups for the side menu filter when filter tray is opened
   */
  useEffect(() => {
    if (
      !checkPGsByCountryPreconditionWithProductType(
        pagesConfig,
        retailerClientState.clientId,
        timeFrame,
        //To add brand filter key if state dont have one, to ensure the side filter load properly
        getNewSideFiltersStateWithBrand().appliedFilters,
        selectedCountries,
        effectiveProductType,
      )
    ) {
      return;
    }

    const filter = tradingAssistantResponseConfig.response[0];

    updatePgsByCountryOnCountrySelectWithProductType(
      pagesConfig,
      retailerClientState.clientId,
      timeFrame,
      filter,
      selectedCountries,
      effectiveProductType,
    );
  }, [timeFrame, selectedCountries]);

  /*
   * Used to get main filters for the selected countries when country filter is changed
   */
  useEffect(() => {
    if (!appliedCountries || !selectedCountries[0]) {
      return;
    }
    const mainFilters = defaultFilters.find((e) => e.country === selectedCountries[0].value);
    if (!mainFilters) {
      return;
    }
    setFilters(mainFilters);

    const currentTimePeriodOptions = initialFiltersState.initialFilters.filter(byCountry(appliedCountries[0].value))[0]
      .timePeriod;

    const fallbackPeriodCombo = getFallbackPeriodCombo(
      { period, periodicity: timeFrame, comparisonPeriod: comparisonPeriod },
      tradingAssistantConfiguration.supportedPeriodicities,
      currentTimePeriodOptions,
      customMonthsPeriodState,
      customWeeksPeriodState,
    );

    if (fallbackPeriodCombo !== null) {
      setPeriod(fallbackPeriodCombo.period);
      setTimeFrame(fallbackPeriodCombo.periodicity);
      setComparisonPeriod(fallbackPeriodCombo.comparisonPeriod);
    }

    getAndUpdatePeriod(
      fallbackPeriodCombo ? fallbackPeriodCombo.period : period,
      currentTimePeriodOptions,
      updatePeriod,
    );

    if (!pagesConfig.isTimePeriodFirstLoad) {
      setCountryChanged(true);
    }
    //To ensure the sidefilter/warning periodicity is shown properly if user first load the page and select pgmodal
    setSelectedCountries(appliedCountries);
  }, [appliedCountries]);

  const toUpdateAddFeatSegFeatValues: UpdateAddFeatSegFeatValues = {
    setSelectedAddFeat: setSelectedAddFeatOpp,
    setSelectedAddFeatValues: setSelectedAddFeatOppValues,
    setSelectedSegFeat: setSelectedSegFeatOpp,
    setSelectedSegFeatValues: setSelectedSegFeatOppValues,
  };

  /*
   * Update selectedProductGroup when selectedFilters change in context
   */
  useEffect(() => {
    updateSelectedProductGroup(
      pagesConfig,
      selectedCountries,
      setSelectedCountries,
      setSelectedProductGroups,
      toUpdateAddFeatSegFeatValues,
    );
  }, [pagesConfig.selectedFilters]);

  useEffect(() => {
    const allFiltersForTradingAssistant = [...pagesConfig.appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters];
    updateSelectedAppliedCountryStateByAppliedFilter(
      selectedCountries,
      setSelectedCountries,
      appliedCountries,
      setAppliedCountries,
      allFiltersForTradingAssistant,
    );

    updateSelectedAppliedPgStateByAppliedFilter(
      selectedProductGroups,
      setSelectedProductGroups,
      appliedProductGroups,
      setAppliedProductGroups,
      allFiltersForTradingAssistant,
    );

    if (isCountryFirstLoad) {
      setIsCountryFirstLoad(false);
    }

    setIsUpdateFeaturesApiCall(true);
  }, [pagesConfig.appliedFilters]);

  useEffect(() => {
    (async () => {
      setPgsByAppliedCountries([]);
      if (
        !checkPGsByCountryPreconditionWithProductType(
          pagesConfig,
          retailerClientState.clientId,
          timeFrame,
          //To add brand filter key if state dont have one, to ensure the side filter load properly
          getNewSideFiltersStateWithBrand().appliedFilters,
          appliedCountries,
          effectiveProductType,
        )
      ) {
        return;
      }

      let availableMasterPgs = await getProductGroupsMasterByCountry(
        retailerClientState.clientId,
        appliedCountries,
        effectiveProductType,
      );
      pagesConfig.setMasterPgsByCountry(availableMasterPgs);
      updatePgsByCountryOnCountryApplyWithProductType(
        pagesConfig,
        retailerClientState.clientId,
        selectedCountries,
        appliedCountries,
        effectiveProductType,
        undefined,
        setPgsByAppliedCountries,
      );
    })();
  }, [appliedCountries]);

  useEffect(() => {
    setDefaultFilters(initialFiltersState.initialFilters);
  }, [initialFiltersState.initialFilters]);

  useEffect(() => {
    (async () => {
      // Useful when the currency is changed, to update priceband filter vals w.r.t. selected currency
      const res = await getCurrencyExchangeRate(period, pagesConfig, timeFrame, initialCurrency[0]);
      pagesConfig.updateCurrencyExchangeRatePerCountry(res);
    })();
  }, [period, timeFrame]);

  useEffect(() => {
    const currencyExchangeRate = getExchangeRate(pagesConfig);
    const appliedFilters = cloneDeep(pagesConfig.appliedFilters);

    if (currencyExchangeRate > 0) {
      // Update PRICEBAND filter labels in applied filters
      appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters.forEach((f) => {
        if (f.filterKey === FilterKeys.PRICEBAND) {
          f.options.forEach((k) => (k.label = formatPriceBandAsPerExchangeRate(k.value, currencyExchangeRate)));
        }
      });
      const selectedFilters = cloneDeep(pagesConfig.selectedFilters);
      // Replace only the PRICEBAND filter in selectedFilters
      const appliedPriceband = appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters.find(
        (f) => f.filterKey === FilterKeys.PRICEBAND,
      );
      if (appliedPriceband) {
        selectedFilters[PageType.TRADING_ASSISTANT].selectedFilters = selectedFilters[
          PageType.TRADING_ASSISTANT
        ].selectedFilters.map((f) => (f.filterKey === FilterKeys.PRICEBAND ? appliedPriceband : f));
        pagesConfig.setSelectedFilters(selectedFilters);
      }

      pagesConfig.setAppliedFilters(appliedFilters);
    }
  }, [pagesConfig.currencyExchangeRatePerCountry]);

  const showsContent = () => !loadingOverlay;

  //Function to change the period when warning periodicity hyperlink is clicked
  const timeFrameOnChangeHandler = (object: { value: Periodicity }): void => {
    const periodicity = object.value;
    setTimeFrame(periodicity);

    const mainFilters = defaultFilters.find((e) => e.country === selectedCountries[0].value)!;

    const timePeriods: BaseTimePeriod[] = mainFilters.timePeriod[periodicity.toLowerCase() as keyof TimePeriod];

    const currentPeriod = timePeriods[getLatestPeriod(timePeriods)];
    setPeriod(currentPeriod);
  };

  const renderContent = () => {
    if (selectedTabLink.value === TradingAssistantTabView.SupplierReview && showsContent()) {
      const bySegmentFeatures = [
        ...(timeFrame === Periodicity.Weekly ? weeklyMonthlyFeatures.weekly : weeklyMonthlyFeatures.monthly).filter(
          (feat) => feat.filterKey !== FilterKeys.BRAND,
        ),
      ];

      return (
        <SupplierReview
          filters={filters}
          timeFrame={timeFrame}
          setTimeFrame={setTimeFrame}
          comparisonPeriod={comparisonPeriod}
          setComparisonPeriod={setComparisonPeriod}
          period={period}
          setPeriod={setPeriod}
          pgListByCountry={pgsByAppliedCountries}
          appliedCountries={appliedCountries}
          appliedProductGroups={appliedProductGroups}
          timeFrameOnChangeHandler={timeFrameOnChangeHandler}
          isLoadingBrands={isFetchingFeatureValues}
          brandFilterOptions={brandFilterOptions}
          features={bySegmentFeatures}
          featureOption={feature?.value === 'Brand' ? INITIAL_BY_SEGMENT_OPTION : feature}
          setFeatureOptions={setFeature}
          viewingBrand={viewingBrand}
          setViewingBrand={setViewingBrandState}
          brandDependencyState={getSaveViewFilters()}
          selectedGrowthType={selectedGrowthType}
          setSelectedGrowthType={setSelectedGrowthType}
          kpi={kpi}
          setKpi={setKpi}
          sortBy={sortBy}
          setSortBy={setSortBy}
          customMonthsPeriodState={customMonthsPeriodState}
          customWeeksPeriodState={customWeeksPeriodState}
          sideFiltersState={sideFiltersState}
          isDeepDiveView={isDeepDiveView}
          setIsDeepDiveView={setIsDeepDiveView}
        />
      );
    }
  };

  const getSaveViewFilters = (): TradingAssistantViewState => {
    const handledTimeFrame = getPeriodicityForMATAndYTD(period, timeFrame);
    return {
      appliedCountries: appliedCountries,
      appliedFilters: pagesConfig.appliedFilters[PageType.TRADING_ASSISTANT].selectedFilters,
      appliedProductGroup: appliedProductGroups,
      brandFilterOptions: brandFilterOptions,
      comparisonPeriod: isBaseTimePeriodFormat(comparisonPeriod)
        ? RelativePeriodLabel.PreviousPeriod
        : RelativePeriodMapping[comparisonPeriod as RelativePeriod],
      defaultFilters: defaultFilters,
      feature: feature,
      filters: filters,
      growthType: selectedGrowthType,
      hitlistInitialFilterState: initialFiltersProductsState,
      hitlistRetailerClientState: hitlistsRetailerClientState,
      initialFilterState: initialFiltersState,
      mainFilter: mainFilterStateMarketPremium,
      period: period,
      periodicity: handledTimeFrame.periodicity,
      matYtdTimePeriodLabel: handledTimeFrame.matYtdLabel,
      pgsByAppliedCountries: pgsByAppliedCountries,
      productMainFilterState: mainFilterStateProducts,
      retailerClientState: {
        ...retailerClientState,
        pageViewing: retailerClientState.pageViewing as PageName | undefined,
      },
      selectedCountries: selectedCountries,
      selectedProductGroup: selectedProductGroups,
      sideFiltersState: sideFiltersState,
      weeklyMonthlyFeatures: weeklyMonthlyFeatures,
      viewingBrand: viewingBrand,
      kpi: kpi,
      sortBy: sortBy,
      isDeepDiveView: isDeepDiveView,
      customMonthsPeriodState: customMonthsPeriodState,
      customWeeksPeriodState: customWeeksPeriodState,
    };
  };

  const getSaveViewTrackingObject = () => {
    return {
      access: pagesConfig.access,
      locationId: appliedCountries.map((country) => country.label),
      pageName: PageName.TRADING_ASSISTANT,
      pageType: PageType.TRADING_ASSISTANT,
      itemGroup: appliedProductGroups.map((pg) => pg.label),
      section: TrackingGeneralProp.SECTION_TRACKING_PROP,
      subSection: SubSection.TRADING_ASSISTANT_SUPPLIER_REVIEW,
    };
  };
  const onFiltersTrayClickHandler = (isExpanded: boolean): void => {
    pagesConfig.toggleFiltersTray(isExpanded);
    removeUnsavedFilters(pagesConfig);
    pagesConfig.setApplyFilters(false);
    pagesConfig.setClearFilters(false);
  };
  const restoreLocalViewProps: TradingAssistantRestoreLocalSavedViewProps = {
    productType: effectiveProductType,
    setAppliedCountries,
    setAppliedProductGroupOptions: setAppliedProductGroups,
    setBrandFilterOptions,
    setDefaultFilters,
    setFeature,
    setFilters,
    setGrowthOption: updateMainFiltersMPGrowthType,
    setIsSubscribed: () => {}, // Placeholder, as this is not used in Trading Assistant
    setPgsByAppliedCountries,
    setPeriod,
    setRelativePeriod: setComparisonPeriod,
    setSelectedCountries,
    setSelectedProductGroupOptions: setSelectedProductGroups,
    setTimeFrame,
    setWeeklyMonthlyFeatures,
    setViewingBrand: setViewingBrandState,
    setKpi: setKpi,
    setSortBy: setSortBy,
    setCustomMonthsPeriodState: updateCustomMonthsPeriodState,
    setCustomWeeksPeriodState: updateCustomWeeksPeriodState,
    setIsDeepDiveView: setIsDeepDiveView,
  };

  useRestoreTradingAssistantSavedView(restoreLocalViewProps);

  return (
    <div>
      <Page>
        <Page.Title>
          <div className="pt-8">
            <PageHeader
              retailerClients={retailerClients}
              onChangeHandler={onChangeHandler}
              productType={effectiveProductType}
            >
              <div className="grid grid-cols-2">
                <div className="col-start-1">
                  <h1 className="text-heading text-4xl font-normal m-0">
                    <span>Trading Assistant</span>
                    <PageInfo
                      title={'Trading Assistant'}
                      content={
                        <section>
                          <p>
                            Dive deeper into your analysis to see who and what is driving the market and your relative
                            positioning.
                          </p>
                          <p>
                            Review what matters (Brand Positioning, Range Review, Pricing) to uncover opportunity and
                            drive your business forwards. Fine tune your analysis by using filters to customise your
                            view by time periods, pricebands, features and more.
                          </p>
                        </section>
                      }
                    />
                  </h1>
                </div>

                <div className="col-start-5">
                  {/* {isAuthorizedUser && isSubscribed && ( */}
                  <div className="flex justify-end">
                    <TradingAssistantCopyViewButton
                      tradingAssistantViewState={getSaveViewFilters()}
                      copyViewTracking={getSaveViewTrackingObject()}
                    />
                    <FilterToggleButton onClick={onFiltersTrayClickHandler} testId="filter-button" />
                  </div>
                  {/* )} */}
                </div>
              </div>
            </PageHeader>
          </div>
        </Page.Title>
        <Page.Content>
          <InfoModal />
          {isAuthorizedUser ? (
            <>
              <TabNav
                selectedLink={selectedTabLink}
                links={[supplierReviewLink]}
                setSelectedLink={setSelectedTabLink}
              />

              <LoadingOverlay loadingOverlay={loadingOverlay} />
              <Suspense fallback={<feedback.DotsLoader />}>{renderContent()}</Suspense>
            </>
          ) : (
            Unauthorized()
          )}
        </Page.Content>
      </Page>
    </div>
  );
};

export default TradingAssistant;
